import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SessionContextMenu } from './SessionContextMenu';
import { useAgentSessions } from './AgentSessionContext';

const getSessionPath = (session, entryPoint, clientId, sessionBasePath) => {
    // Base explícita del provider (chat embebido en el tab IA de la ficha v2): navegar ahí
    // adentro, sin rebotar por la ruta legacy.
    if (sessionBasePath) return `${sessionBasePath}/${session.session_id}`;
    if (entryPoint === 'client') return `/client/${clientId}/agent/${session.session_id}`;
    if (entryPoint === 'portal') return `/portal/agent/${session.session_id}`;
    return `/agent/chat/${session.session_id}`;
};

const VisibilityIcon = ({ visibility }) => {
    if (visibility === 'TEAM') {
        return <i className="fas fa-users agent-sidebar__vis-icon agent-sidebar__vis-icon--team" title="Compartida"></i>;
    }
    if (visibility === 'TEAM_CLIENT') {
        return (
            <span className="agent-sidebar__vis-icons" title="Compartida con cliente">
                <i className="fas fa-users"></i>
                <i className="fas fa-globe-americas agent-sidebar__vis-icon-overlay"></i>
            </span>
        );
    }
    return null;
};

export const SessionItem = ({ session, onRename, onShare, onArchive, onDelete }) => {
    const { t } = useTranslation();
    const { activeSessionId, entryPoint, clientId, sessionBasePath, userId } = useAgentSessions();
    const [menuPos, setMenuPos] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState('');
    const { renameSession } = useAgentSessions();
    const menuBtnRef = useRef(null);

    const isActive = activeSessionId === session.session_id;
    const isMine = String(session.creator_id) === String(userId);
    const isArchived = session.is_archived;
    const path = getSessionPath(session, entryPoint, clientId, sessionBasePath);

    const handleMenuClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (menuPos) {
            setMenuPos(null);
            return;
        }
        const rect = menuBtnRef.current.getBoundingClientRect();
        setMenuPos({ top: rect.bottom + 4, left: rect.left });
    };

    const handleStartRename = () => {
        setIsEditing(true);
        setEditValue(session.name);
    };

    const handleSubmitRename = () => {
        const trimmed = editValue.trim();
        if (trimmed && trimmed !== session.name) {
            renameSession(session.session_id, trimmed);
        }
        setIsEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); handleSubmitRename(); }
        if (e.key === 'Escape') setIsEditing(false);
    };

    const itemClasses = [
        'agent-sidebar__session-item',
        isActive && 'agent-sidebar__session-item--active',
        isArchived && 'agent-sidebar__session-item--archived',
    ].filter(Boolean).join(' ');

    return (
        <div className={itemClasses}>
            {isEditing ? (
                <input
                    type="text"
                    className="agent-sidebar__rename-input"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleSubmitRename}
                    autoFocus
                />
            ) : (
                <>
                    <Link to={path} className="agent-sidebar__session-link">
                        <div className="agent-sidebar__session-name-row">
                            <VisibilityIcon visibility={session.visibility} />
                            <span className="agent-sidebar__session-name">{session.name}</span>
                        </div>
                        {!isMine && session.creator_name && (
                            <span className="agent-sidebar__session-creator">
                                {t('agent_chat.by_user', { name: session.creator_name })}
                            </span>
                        )}
                    </Link>
                    {entryPoint === 'global' && session.client_name && (
                        <span className="agent-session-badge">{session.client_name}</span>
                    )}
                    <button
                        ref={menuBtnRef}
                        className="agent-sidebar__menu-btn"
                        onClick={handleMenuClick}
                    >
                        <i className="fas fa-ellipsis-v"></i>
                    </button>
                </>
            )}

            {menuPos && (
                <SessionContextMenu
                    session={session}
                    position={menuPos}
                    isMine={isMine}
                    onRename={() => handleStartRename()}
                    onShare={() => onShare(session)}
                    onArchive={() => onArchive(session)}
                    onDelete={() => onDelete(session)}
                    onClose={() => setMenuPos(null)}
                />
            )}
        </div>
    );
};
